import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { UserContext, SnackbarContext, EngInfoContext } from './userContext';
import { BackDropLoad } from './LoadingBackDrop';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Phone: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72a2 2 0 0 1 1.72 2z" />
    </svg>
  ),
  Lock: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Eye: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m1 1 22 22" />
      <path d="M17.94 17.94A10.06 10.06 0 0 1 12 19c-7 0-10-7-10-7a18 18 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a18 18 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
    </svg>
  ),
  Message: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Spinner: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="40 60" strokeLinecap="round" />
    </svg>
  ),
  Verified: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M9 12l2 2 4-4" />
      <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
    </svg>
  ),
  Bolt: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  Globe: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" />
    </svg>
  ),
};

/* ─────────── Reusable Field - amber theme ─────────── */

function Field({
  label, icon, type = 'text', value, onChange, error, errorMsg, helper, required,
  autoComplete, inputMode, maxLength, trailing, autoFocus, placeholder,
}) {
  const [focused, setFocused] = useState(false);
  const showError = !!error;

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <div
        className={`
          relative flex h-12 items-center gap-2.5 rounded-lg px-3.5
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          ${showError
            ? 'border-red-400 shadow-[0_0_0_3px_rgba(248,113,113,0.16)]'
            : focused
              ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
              : 'border-amber/22 hover:border-amber/55'}
        `}
      >
        {icon && (
          <span className={`shrink-0 transition-colors duration-200 ${showError ? 'text-red-400' : focused ? 'text-amber' : 'text-amber/70'}`}>
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-[14px] font-medium text-fg-primary placeholder:text-fg-muted outline-none border-0 p-0"
        />
        {trailing}
      </div>
      <div className={`mt-1 min-h-[16px] text-[11px] leading-tight ${showError ? 'text-red-400' : 'text-fg-tertiary'}`}>
        {showError && errorMsg ? errorMsg : (helper || ' ')}
      </div>
    </div>
  );
}

const EyeToggle = ({ show, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={show ? 'Hide password' : 'Show password'}
    className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-tertiary hover:bg-surface-overlay hover:text-amber transition-colors duration-200"
  >
    {show ? <Icon.EyeOff className="h-[17px] w-[17px]" /> : <Icon.Eye className="h-[17px] w-[17px]" />}
  </button>
);

/* ─────────── Modal ─────────── */

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/72 backdrop-blur-md animate-[fadeIn_180ms_ease-out]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="
          relative w-full max-w-md overflow-hidden rounded-2xl
          bg-surface-paper border border-amber/30
          p-6 sm:p-7
          animate-[fadeIn_180ms_ease-out]
          shadow-[0_24px_64px_-12px_rgba(0,0,0,0.85),0_0_64px_-8px_rgba(251,191,36,0.22)]
        "
      >
        <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
        {children}
      </div>
    </div>
  );
}

/* ─────────── Brand panel (shared with Signup conceptually) ─────────── */

function BrandPanel() {
  return (
    <aside className="relative hidden md:flex isolate overflow-hidden bg-amber-gradient">
      {/* noise */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* dark grid mask */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.10]"
        style={{
          backgroundImage: 'linear-gradient(rgba(10,10,11,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,11,0.6) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 60% 70% at 50% 50%, #000, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 70% at 50% 50%, #000, transparent 80%)',
        }}
      />
      {/* dark radial accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(700px 360px at 100% 100%, rgba(10,10,11,0.18), transparent 60%)' }}
      />
      {/* floating amber-tinted orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-12 -left-16 h-72 w-72 rounded-full opacity-50 animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-12 h-80 w-80 rounded-full opacity-40"
        style={{ background: 'radial-gradient(circle, rgba(10,10,11,0.18), transparent 70%)' }}
      />

      <div className="relative flex flex-col justify-center p-10 lg:p-14 w-full">
        {/* Headline */}
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-ink/24 bg-ink/8 px-3 py-1.5">
            <span aria-hidden className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink opacity-50" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-ink" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink">Welcome back</span>
          </div>
          <h1 className="text-[36px] lg:text-[48px] font-black leading-[1.02] tracking-[-0.038em] text-ink">
            Pick up where<br />you left off.
          </h1>
          <p className="mt-5 max-w-[480px] text-[15px] leading-[1.6] text-ink/80">
            Sign in to continue connecting with verified construction partners,
            browse engineering reference info, and access your saved bookmarks.
          </p>

          {/* Stats strip */}
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-[440px]">
            {[
              { n: '12K+',  l: 'Members' },
              { n: '28',    l: 'States' },
              { n: '200+',  l: 'Categories' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl border border-ink/15 bg-ink/8 px-3 py-2.5 backdrop-blur-sm">
                <div className="text-[20px] font-black tracking-[-0.02em] text-ink leading-none">{s.n}</div>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink/65">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ═══════════════════ Login Component ═══════════════════ */

export const Login = () => {
  const history = useNavigate();
  const { setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);
  const { setEngInfoList } = useContext(EngInfoContext);

  const [num, setNum] = useState('');
  const [frnum, setFrNum] = useState('');
  const [pass, setPass] = useState('');
  const [remember, setRemember] = useState(false);
  const [wait, setWait] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showPassNew, setShowPassNew] = useState(false);
  const [error, setError] = useState(0);

  const [otp, setOtp] = useState('');
  const [otptoken, setOtpToken] = useState('');
  const [newpass, setNewPass] = useState('');
  const [renewpass, setReNewPass] = useState('');

  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const getEngInfodata = async (tkn, uid) => {
    try {
      const res = await Axios.post('/new_apis/get_bookmarks', { user_id: uid }, { headers: { tkn } });
      if (res.data.status === 1) setEngInfoList(res.data.data.map((d) => d.id));
    } catch { /* noop */ }
  };

  const loginNow = (e) => {
    e.preventDefault();
    setError(0);
    if (num.length !== 10) {
      setError(1);
      setSnack({ message: 'Enter a valid 10-digit mobile number.', type: 'error', open: true });
      return;
    }
    if (pass.length < 3) {
      setError(2);
      setSnack({ message: 'Enter your password.', type: 'error', open: true });
      return;
    }
    setWait(true);
    Axios.post('/new_apis/new_login', { num, pass }, { headers: { key } }).then((res) => {
      if (res.data.status) {
        setWait(false);
        setUser(res.data.user_data);
        localStorage.setItem('estkn', res.data.token);
        getEngInfodata(res.data.token, res.data.user_data.user_id);
        setSnack({ message: res.data.msg, type: 'success', open: true, direction: 'left' });
        history(res.data.user_data.profile_done === '0' ? '/myaccount/complete_profile' : '/myaccount/profile');
      } else {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    }).catch(() => {
      setWait(false);
      setSnack({ message: 'Network error. Please try again.', type: 'error', open: true });
    });
  };

  const generateotp = (e) => {
    if (e) e.preventDefault();
    setError(0);
    if (frnum.length !== 10) {
      setError(3);
      setSnack({ message: 'Enter a valid 10-digit mobile number.', type: 'error', open: true });
      return;
    }
    setForgotOpen(false);
    setWait(true);
    Axios.post('/new_apis/checkusermobile', { mob: frnum }, { headers: { key } }).then((res) => {
      if (res.data.status === 1) {
        setWait(false);
        setOtpToken(res.data.otptoken);
        setResetOpen(true);
        setSnack({ message: res.data.msg, type: 'success', open: true });
      } else {
        setWait(false);
        setForgotOpen(true);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    });
  };

  const validateotp = (e) => {
    if (e) e.preventDefault();
    setError(0);
    if (otp === '') {
      setError(4);
      setSnack({ message: 'Enter the OTP sent to your mobile.', type: 'error', open: true });
      return;
    }
    if (newpass.length < 5) {
      setError(5);
      setSnack({ message: 'Password must be at least 5 characters.', type: 'error', open: true });
      return;
    }
    if (newpass !== renewpass) {
      setError(6);
      setSnack({ message: 'Passwords do not match.', type: 'error', open: true });
      return;
    }
    setWait(true);
    Axios.post('/new_apis/forgotpassword_validateotp',
      { mob: frnum, otp, otptoken, pass: newpass },
      { headers: { key } },
    ).then((res) => {
      if (res.data.status === 1) {
        setWait(false);
        setResetOpen(false);
        setSnack({ message: res.data.msg, type: 'success', open: true });
      } else {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    });
  };

  return (
    <>
      <Helmet>
        <title>Login | ESAY - Engineering Sources Around You</title>
      </Helmet>

      <BackDropLoad wait={wait} />

      {/* ═════ Split-screen layout ═════ */}
      <div className="grid grid-cols-1 md:grid-cols-2 min-h-[calc(100vh-56px)]">

        <BrandPanel />

        {/* RIGHT - Form panel */}
        <main className="relative flex items-center justify-center px-5 py-10 sm:px-8 md:px-10 lg:px-14 bg-surface-base overflow-hidden">
          {/* subtle radial */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(700px 320px at 50% -10%, rgba(251,191,36,0.10), transparent 55%), radial-gradient(500px 280px at 100% 110%, rgba(251,191,36,0.06), transparent 60%)',
            }}
          />

          <div className="relative w-full max-w-[440px]">
            {/* Mobile-only brand strip + signup CTA */}
            <div className="md:hidden mb-7 flex items-center justify-between">
              <RouterLink to="/" className="inline-flex items-center gap-2.5">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber shadow-amber-glow">
                  <img src="/assets/ESAY.png" alt="" className="h-6 w-6 object-contain" />
                </span>
                <span className="text-[16px] font-extrabold tracking-[-0.02em] text-fg-primary leading-none">ESAY</span>
              </RouterLink>
              <RouterLink
                to="/signup"
                className="inline-flex items-center gap-1 rounded-full border border-amber/40 bg-amber/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber hover:bg-amber/20"
              >
                Sign up
              </RouterLink>
            </div>

            {/* Header */}
            <div className="mb-6">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/10 px-3 py-1.5">
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Sign in</span>
              </div>
              <h2 className="text-[26px] sm:text-[30px] font-bold leading-[1.12] tracking-[-0.025em] text-fg-primary">
                Welcome back<span className="text-amber">.</span>
              </h2>
              <div aria-hidden className="mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
              <p className="mt-3 text-[13.5px] text-fg-secondary leading-relaxed">
                Sign in with your registered mobile number.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={loginNow} noValidate className="flex flex-col gap-1">
              <Field
                label="Mobile number"
                required
                type="tel"
                icon={<Icon.Phone className="h-[18px] w-[18px]" />}
                value={num}
                onChange={(e) => setNum(e.target.value.replace(/\D/g, '').slice(0, 10))}
                error={error === 1}
                errorMsg="Enter a valid 10-digit mobile number."
                helper=" "
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
                placeholder="9876543210"
                autoFocus
              />

              <Field
                label="Password"
                required
                type={showPass ? 'text' : 'password'}
                icon={<Icon.Lock className="h-[18px] w-[18px]" />}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                error={error === 2}
                errorMsg="Please enter your password."
                helper=" "
                autoComplete="current-password"
                trailing={<EyeToggle show={showPass} onToggle={() => setShowPass((v) => !v)} />}
              />

              {/* Remember + Forgot inline */}
              <div className="-mt-1 mb-2 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none group">
                  <span
                    style={
                      remember
                        ? { backgroundColor: '#FBBF24', borderColor: '#FBBF24' }
                        : { borderColor: '#FBBF24', backgroundColor: 'rgba(251,191,36,0.10)' }
                    }
                    className="
                      relative inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center
                      rounded border-2
                      transition-[background-color,border-color] duration-150 ease-out-soft
                    "
                  >
                    {remember && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#0A0A0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-[12px] w-[12px]" aria-hidden>
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    )}
                  </span>
                  <input type="checkbox" checked={remember} onChange={() => setRemember((v) => !v)} className="sr-only" />
                  <span className="text-[12px] font-medium text-fg-secondary group-hover:text-fg-primary transition-colors duration-150">
                    Remember me
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-[12px] font-bold text-amber hover:text-amber-light transition-colors duration-200"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={wait}
                className={`
                  group mt-3 inline-flex h-12 w-full items-center justify-center gap-2
                  rounded-lg
                  text-[12.5px] font-bold uppercase tracking-[0.12em]
                  transition-all duration-200 ease-out-soft
                  ${wait
                    ? 'bg-amber/30 text-ink/50 cursor-wait'
                    : 'bg-amber text-ink hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.32),0_10px_28px_-8px_rgba(251,191,36,0.55)] active:scale-[0.98]'}
                `}
              >
                {wait ? (
                  <>
                    <Icon.Spinner className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <Icon.ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* New to ESAY */}
            <div className="
              mt-7 pt-5 border-t border-amber/15 text-center
              flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-3
            ">
              <span className="text-[13px] text-fg-secondary">New to ESAY?</span>
              <RouterLink
                to="/signup"
                className="
                  inline-flex h-10 items-center justify-center gap-1 rounded-lg px-4
                  text-[12px] font-bold uppercase tracking-[0.1em]
                  border border-amber/40 text-amber
                  transition-[background-color,border-color] duration-200
                  hover:bg-amber/10 hover:border-amber/70
                "
              >
                Create an account
                <Icon.ArrowRight className="h-3.5 w-3.5" />
              </RouterLink>
            </div>
          </div>
        </main>
      </div>

      {/* ═════ Forgot password - enter mobile ═════ */}
      <Modal open={forgotOpen} onClose={() => setForgotOpen(false)}>
        <button
          type="button"
          onClick={() => setForgotOpen(false)}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-fg-tertiary hover:bg-surface-overlay hover:text-amber transition-colors duration-200"
        >
          <Icon.Close className="h-4 w-4" />
        </button>

        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/10 px-2.5 py-1">
          <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Reset password</span>
        </div>
        <h3 className="mt-3 text-[20px] font-bold tracking-[-0.018em] text-fg-primary leading-tight">
          Forgot your password?
        </h3>
        <p className="mt-1.5 text-[13px] text-fg-secondary leading-relaxed">
          Enter the mobile number linked to your account. We'll send a one-time code.
        </p>

        <form onSubmit={generateotp} noValidate className="mt-5">
          <Field
            label="Mobile number"
            required
            type="tel"
            icon={<Icon.Phone className="h-[18px] w-[18px]" />}
            value={frnum}
            onChange={(e) => setFrNum(e.target.value.replace(/\D/g, '').slice(0, 10))}
            error={error === 3}
            errorMsg="Enter a valid 10-digit mobile number."
            helper=" "
            inputMode="numeric"
            maxLength={10}
            autoFocus
            placeholder="9876543210"
          />

          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setForgotOpen(false)}
              className="
                inline-flex h-10 items-center justify-center rounded-lg px-4
                text-[12px] font-bold uppercase tracking-[0.08em]
                border border-border-subtle text-fg-secondary
                transition-[background-color,border-color,color] duration-200
                hover:bg-surface-overlay hover:text-fg-primary hover:border-amber/30
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={wait}
              className="
                group inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                hover:bg-amber-light
                hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                active:scale-[0.98]
                disabled:opacity-60 disabled:pointer-events-none
              "
            >
              Send OTP
              <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </form>
      </Modal>

      {/* ═════ Reset password - OTP + new password ═════ */}
      <Modal open={resetOpen} onClose={() => setResetOpen(false)}>
        <button
          type="button"
          onClick={() => setResetOpen(false)}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-fg-tertiary hover:bg-surface-overlay hover:text-amber transition-colors duration-200"
        >
          <Icon.Close className="h-4 w-4" />
        </button>

        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/10 px-2.5 py-1">
          <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Set new password</span>
        </div>
        <h3 className="mt-3 text-[20px] font-bold tracking-[-0.018em] text-fg-primary leading-tight">
          Verify and reset
        </h3>
        <p className="mt-1.5 text-[13px] text-fg-secondary leading-relaxed">
          Enter the OTP we sent and choose a new password.
        </p>

        <form onSubmit={validateotp} noValidate className="mt-5 flex flex-col gap-1">
          <Field
            label="OTP code"
            required
            type="tel"
            icon={<Icon.Message className="h-[18px] w-[18px]" />}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            error={error === 4}
            errorMsg="Please enter the OTP."
            helper=" "
            inputMode="numeric"
            autoFocus
            placeholder="6-digit code"
          />

          <Field
            label="New password"
            required
            type={showPassNew ? 'text' : 'password'}
            icon={<Icon.Lock className="h-[18px] w-[18px]" />}
            value={newpass}
            onChange={(e) => setNewPass(e.target.value)}
            error={error === 5}
            errorMsg="At least 5 characters."
            helper="At least 5 characters."
            autoComplete="new-password"
            trailing={<EyeToggle show={showPassNew} onToggle={() => setShowPassNew((v) => !v)} />}
          />

          <Field
            label="Confirm new password"
            required
            type={showPassNew ? 'text' : 'password'}
            icon={<Icon.Lock className="h-[18px] w-[18px]" />}
            value={renewpass}
            onChange={(e) => setReNewPass(e.target.value)}
            error={error === 6}
            errorMsg="Passwords do not match."
            helper=" "
            autoComplete="new-password"
          />

          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setResetOpen(false)}
              className="
                inline-flex h-10 items-center justify-center rounded-lg px-4
                text-[12px] font-bold uppercase tracking-[0.08em]
                border border-border-subtle text-fg-secondary
                transition-[background-color,border-color,color] duration-200
                hover:bg-surface-overlay hover:text-fg-primary hover:border-amber/30
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={wait}
              className="
                group inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                hover:bg-amber-light
                hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                active:scale-[0.98]
                disabled:opacity-60 disabled:pointer-events-none
              "
            >
              Update password
              <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};
